import UserService from "@/Services/UserService";

export type UserProfileRecord = Record<string, unknown>;

/**
 * Caches the profile per user guid so player load, order summary, and mail
 * events share a single network call per session.
 */
const profileCache = new Map<string, UserProfileRecord | null>();
const profileRequests = new Map<string, Promise<UserProfileRecord | null>>();

function asRecord(value: unknown): UserProfileRecord | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as UserProfileRecord)
    : null;
}

function normalizeProfileGuid(userGuid: string): string {
  return userGuid.trim().toLowerCase();
}

export function fetchUserProfileAbridged(
  userGuid: string,
): Promise<UserProfileRecord | null> {
  const cacheKey = normalizeProfileGuid(userGuid);
  if (!cacheKey) {
    return Promise.resolve(null);
  }

  if (profileCache.has(cacheKey)) {
    return Promise.resolve(profileCache.get(cacheKey) ?? null);
  }

  const inFlight = profileRequests.get(cacheKey);
  if (inFlight) {
    return inFlight;
  }

  const request = UserService.getUserProfileAbridged(cacheKey)
    .then((response: unknown) => {
      const profile = asRecord(response);
      if (profile) {
        profileCache.set(cacheKey, profile);
      }
      return profile;
    })
    .catch((error: unknown) => {
      profileRequests.delete(cacheKey);
      throw error;
    })
    .finally(() => {
      profileRequests.delete(cacheKey);
    });

  profileRequests.set(cacheKey, request);
  return request;
}

export function readProfileString(
  profile: UserProfileRecord | null,
  key: string,
): string {
  const value = profile?.[key];
  return typeof value === "string" ? value.trim() : "";
}

/** First token of the email local-part, title-cased (e.g. john.doe@… → John). */
export function resolveEmailLocalFirstName(email: string): string {
  const localPart = email.split("@")[0]?.trim() ?? "";
  if (!localPart) return "";

  const token = localPart.split(/[._-]/)[0] ?? localPart;
  if (!token) return "";

  return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
}

/** Profile first name, then email local-part, then first token of fullName. */
export function resolveProfileFirstName(profile: UserProfileRecord | null): string {
  const firstName = readProfileString(profile, "firstName");
  if (firstName) return firstName;

  const emailFirstName = resolveEmailLocalFirstName(readProfileString(profile, "email"));
  if (emailFirstName) return emailFirstName;

  return readProfileString(profile, "fullName").split(/\s+/)[0] ?? "";
}

/** Display name for header menus (e.g. "John D."). */
export function resolveProfileDisplayName(profile: UserProfileRecord | null): string {
  const firstName = resolveProfileFirstName(profile);
  if (!firstName) return "User";

  const lastName = readProfileString(profile, "lastName");
  if (lastName) {
    return `${firstName} ${lastName.charAt(0).toUpperCase()}.`;
  }

  return firstName;
}

export function resolveProfileInitial(
  profile: UserProfileRecord | null,
  displayName?: string,
): string {
  const name = displayName ?? resolveProfileDisplayName(profile);
  return (name.charAt(0) || "U").toUpperCase();
}
